/* eslint-disable import/max-dependencies */
import type { ElementType, FC } from "react";
import type React from "react";
import { Table, TableFilters, withHookPropsState } from "@fas/cpa-cabinet-ui";
import { usePagination } from "@fas/cpa-state-manager/redux/hooks";
import { withStyles } from "@mui/styles";
import type { Column } from "@fas/cpa-cabinet-ui/lib/Table/Table.types";
import FilterButton from "@fas/cpa-cabinet-ui/lib/Table/FilterButton";
import { useSelector } from "react-redux";
import { getLoadingState } from "@fas/cpa-state-manager/services/selectors/loading";
import { Box } from "@mui/material";
import { getTablePendingFields } from "@fas/cpa-state-manager/services/selectors/table";
import Pagination from "../Pagination";
import { useReportTable, useTableFilters } from "../../hooks";
import { reportTableStyles, tableFiltersStyles } from "../../styles";
import EditReportButton from "../EditReportButton";
import withNoData from "../WithNoData";
import NoData from "../NoData";
import ReportSkeletonLoading from "./ReportSkeletonLoading";
import { useTableNoData } from "../../hooks/useTableNoData";
import { useApplyFilters } from "../../hooks/useTableFilters/useTableFilters";
import ApplyButton from "./ApplyButton";
import filterComponentMap from "./filterComponentMap";
import ReportEmptyIcon from "../../icons/ReportEmpty";

type Props = {
  tableKey: string;
  configureComponent: React.JSX.Element;
  headerTotal?: React.JSX.Element;
  bodyTotal?: React.JSX.Element;
  footerTotal?: React.JSX.Element;
  columnsMap: Record<string, Column<any>>;
};

const ReportWithNoData = withHookPropsState(
  withNoData(
    (p: { isFetched?: boolean }) => null,
    ({ isFetched }: { isFetched?: boolean }) => (
      <Box
        sx={{
          height: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <NoData
          display={isFetched ? "flex" : "none"}
          Icon={
            <Box>
              <ReportEmptyIcon />
            </Box>
          }
          headerText="No data available for the selected filters"
          text="Select the right filters to see your results, or try new cool offers to give your stats a boost!"
        />
      </Box>
    )
  ),
  useTableNoData
);

const Filters = withHookPropsState(TableFilters, useTableFilters);
const ReportTable = withHookPropsState(Table<object>, useReportTable<object>);
const ReportPagination = withHookPropsState(Pagination, usePagination);
const StyledFilters = withStyles(tableFiltersStyles)(Filters);
const StyledTable = withStyles(reportTableStyles)(ReportTable);
const StyledFilterButton = withHookPropsState(withStyles((theme) => ({
  iconButton: {
    "&.MuiIconButton-colorPrimary": {
      color: "rgba(113, 216, 160, 1)",
    },
    "&.MuiIconButton-colorPrimary.Mui-disabled": {
      color: "rgba(113, 216, 160, 0.3)",
    },
  },
  popoverPaper: {
    marginTop: theme.spacing(1),
    width: "360px",
    minWidth: "fit-content",
    borderRadius: theme.shape.cardBorderRadius,
    boxShadow: "0px 0px 50px 0px rgba(255, 255, 255, 0.2)",
  },
}))(FilterButton), ({ tableKey }) => ({
  onApply: useApplyFilters({ tableKey }),
}));
const ReportLoading = withHookPropsState(
  ReportSkeletonLoading,
  ({ tableKey }: { tableKey: string }) => {
    const isLoading = useSelector((state) => getLoadingState(state, tableKey));
    return {
      isLoading,
      fieldsLength: useSelector((state) =>
        getTablePendingFields(state, tableKey)
      ).length,
    };
  }
);
const Apply = withHookPropsState(ApplyButton, ({ tableKey }) => ({
  onApply: useApplyFilters({
    tableKey,
  }),
}));

const Report: FC<Props> = ({
  tableKey,
  configureComponent,
  columnsMap,
  headerTotal,
  bodyTotal,
  footerTotal,
}) => {
  // eslint-disable-next-line react/no-unstable-nested-components
  const FilterButtonComponent = (props: any) => (
    <StyledFilterButton {...props} hookProps={{ tableKey }} />
  );

  return (
    <>
      {headerTotal}
      <StyledFilters
        hookProps={{
          tableKey,
        }}
        filterComponentMap={filterComponentMap}
      >
        <Apply
          hookProps={{
            tableKey,
          }}
        />
        <EditReportButton>
          {configureComponent}
        </EditReportButton>
      </StyledFilters>
      <StyledTable
        hookProps={{
          tableKey,
          columnsMap,
        }}
        FilterButton={FilterButtonComponent}
      >
        <ReportLoading
          hookProps={{
            tableKey,
          }}
        />
        {bodyTotal}
      </StyledTable>
      {footerTotal}
      <ReportWithNoData
        hookProps={{
          tableKey,
        }}
      />
      <ReportPagination hookProps={tableKey} />
    </>
  );
};

export default Report;
